<template>
  <transition name="modal">
    <div v-if="show" class="modal-mask" @click.self="closeModal">
      <div class="modal-wrapper">
        <div class="modal-container">
          <!-- Header -->
          <div class="modal-header">
            <div class="flex items-start gap-3">
              <div
                class="flex-shrink-0 w-12 h-12 rounded-full bg-yellow-100 flex items-center justify-center"
              >
                <svg
                  class="w-6 h-6 text-yellow-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                  />
                </svg>
              </div>
              <div class="flex-1">
                <h3 class="text-xl font-bold text-gray-900">
                  {{ $t("message.unpaid_order_comment_modal.title") }}
                </h3>
                <p class="text-sm text-gray-600 mt-1">
                  {{ $t("message.unpaid_order_comment_modal.subtitle") }}
                </p>
              </div>
            </div>
            <button
              @click="closeModal"
              class="text-gray-400 hover:text-gray-600 transition-colors ml-4"
            >
              <svg
                class="w-6 h-6"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>

          <!-- Body -->
          <div class="modal-body">
            <!-- Warning Box -->
            <div
              class="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-6 rounded"
            >
              <div class="flex items-start">
                <div class="flex-shrink-0">
                  <svg
                    class="h-5 w-5 text-yellow-400"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fill-rule="evenodd"
                      d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                      clip-rule="evenodd"
                    />
                  </svg>
                </div>
                <div class="ml-3">
                  <p class="text-sm text-yellow-800 font-medium">
                    <strong>{{
                      $t("message.unpaid_order_comment_modal.warning_strong")
                    }}</strong>
                    {{ $t("message.unpaid_order_comment_modal.warning_text") }}
                  </p>
                </div>
              </div>
            </div>

            <!-- Comment Input -->
            <div class="mb-4">
              <label
                for="comment"
                class="block text-sm font-semibold text-gray-700 mb-2"
              >
                {{ $t("message.unpaid_order_comment_modal.reason_label") }}
                <span class="text-red-600">{{
                  $t("message.unpaid_order_comment_modal.required")
                }}</span>
              </label>
              <textarea
                id="comment"
                ref="commentInput"
                v-model="comment"
                rows="4"
                :placeholder="
                  $t('message.unpaid_order_comment_modal.placeholder')
                "
                class="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none transition-colors"
                :class="{
                  'border-red-500 focus:ring-red-500 focus:border-red-500': showError
                }"
                @input="showError = false"
              ></textarea>
              <div
                v-if="showError"
                class="mt-2 text-sm text-red-600 flex items-center gap-1"
              >
                <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                  <path
                    fill-rule="evenodd"
                    d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                    clip-rule="evenodd"
                  />
                </svg>
                {{ $t("message.unpaid_order_comment_modal.error_required") }}
              </div>
              <div class="mt-2 text-xs text-gray-500">
                {{ $t("message.unpaid_order_comment_modal.character_count") }}
                {{ comment.length }} / 1000
              </div>
            </div>

            <!-- Quick Shortcuts -->
            <div class="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <p class="text-sm font-medium text-blue-900 mb-3">
                {{ $t("message.unpaid_order_comment_modal.quick_shortcuts") }}
              </p>
              <div class="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <button
                  v-for="shortcut in shortcuts"
                  :key="shortcut.key"
                  @click="
                    selectShortcut(
                      $t(
                        `message.unpaid_order_comment_modal.shortcuts.${shortcut.key}`
                      )
                    )
                  "
                  class="px-4 py-2 bg-white border border-blue-300 rounded-lg hover:bg-blue-100 hover:border-blue-400 transition-colors text-left text-sm text-blue-900 font-medium flex items-center gap-2"
                >
                  <span class="text-lg">{{ shortcut.icon }}</span>
                  <span>{{
                    $t(
                      `message.unpaid_order_comment_modal.shortcuts.${shortcut.key}`
                    )
                  }}</span>
                </button>
              </div>
            </div>
          </div>

          <!-- Footer -->
          <div class="modal-footer">
            <button
              @click="closeModal"
              class="px-6 py-2.5 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors font-medium"
            >
              {{ $t("message.unpaid_order_comment_modal.cancel") }}
            </button>
            <button
              @click="submit"
              class="px-6 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center gap-2"
              :disabled="!comment.trim() || comment.length > 1000"
            >
              <svg
                v-if="comment.trim() && comment.length <= 1000"
                class="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M5 13l4 4L19 7"
                />
              </svg>
              {{ $t("message.unpaid_order_comment_modal.continue") }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </transition>
</template>

<script>
export default {
  name: "UnpaidOrderCommentModal",
  props: {
    show: {
      type: Boolean,
      required: true
    }
  },
  data() {
    return {
      comment: "",
      showError: false,
      shortcuts: [
        {
          icon: "💳",
          key: "credit_terms"
        },
        {
          icon: "💵",
          key: "cash_payment"
        },
        {
          icon: "🍀",
          key: "clover_payment"
        },
        {
          icon: "🅿️",
          key: "paypal_payment"
        },
        {
          icon: "📦",
          key: "cod"
        }
      ]
    };
  },
  watch: {
    show(newVal) {
      if (newVal) {
        // Reset state when modal opens
        this.comment = "";
        this.showError = false;
        // Focus textarea after modal animation
        this.$nextTick(() => {
          setTimeout(() => {
            if (this.$refs.commentInput) {
              this.$refs.commentInput.focus();
            }
          }, 100);
        });
      }
    }
  },
  methods: {
    closeModal() {
      this.$emit("close");
    },
    selectShortcut(text) {
      this.comment = text;
      this.showError = false;
      // Focus the textarea so user can see the text and edit if needed
      this.$nextTick(() => {
        if (this.$refs.commentInput) {
          this.$refs.commentInput.focus();
        }
      });
    },
    submit() {
      if (!this.comment.trim()) {
        this.showError = true;
        return;
      }

      if (this.comment.length > 1000) {
        this.showError = true;
        return;
      }

      this.$emit("submit", this.comment.trim());
    }
  }
};
</script>

<style scoped>
.modal-mask {
  position: fixed;
  z-index: 9998;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  transition: opacity 0.3s ease;
  backdrop-filter: blur(2px);
}

.modal-wrapper {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  padding: 1rem;
}

.modal-container {
  background-color: white;
  border-radius: 12px;
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
  max-width: 600px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
}

.modal-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  padding: 1.5rem;
  border-bottom: 1px solid #e5e7eb;
  background: linear-gradient(to bottom, #ffffff, #fafafa);
}

.modal-body {
  padding: 1.5rem;
  flex: 1;
  overflow-y: auto;
}

.modal-footer {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 0.75rem;
  padding: 1.25rem 1.5rem;
  border-top: 1px solid #e5e7eb;
  background-color: #f9fafb;
}

/* Modal transition */
.modal-enter-active,
.modal-leave-active {
  transition: opacity 0.3s ease;
}

.modal-enter-from,
.modal-leave-to {
  opacity: 0;
}

.modal-enter-active .modal-container,
.modal-leave-active .modal-container {
  transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.modal-enter-from .modal-container,
.modal-leave-to .modal-container {
  transform: scale(0.95) translateY(-20px);
}

/* Scrollbar styling */
.modal-container::-webkit-scrollbar {
  width: 8px;
}

.modal-container::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 10px;
}

.modal-container::-webkit-scrollbar-thumb {
  background: #888;
  border-radius: 10px;
}

.modal-container::-webkit-scrollbar-thumb:hover {
  background: #555;
}

/* Mobile responsive */
@media (max-width: 640px) {
  .modal-wrapper {
    padding: 0.5rem;
  }

  .modal-container {
    max-width: 100%;
    max-height: 95vh;
    border-radius: 8px;
  }

  .modal-header,
  .modal-body,
  .modal-footer {
    padding: 1rem;
  }

  .modal-footer {
    flex-direction: column;
    gap: 0.5rem;
  }

  .modal-footer button {
    width: 100%;
    justify-content: center;
  }
}
</style>
